/* Heat preference prompt — Feb-2026 (indie-conv batch2 pt3).
 *
 * Fires once for signed-in bookstore readers whose account has never
 * recorded a `preferred_heat_max`. The chosen value is persisted to
 * the user record via PUT /bookstore/users/me/preferences and read by
 * GenrePage as the initial `heatMax` for any shelf that hasn't been
 * customised in localStorage yet.
 *
 * A localStorage marker suppresses the dialog for anonymous readers
 * and for readers who dismiss without picking — we do NOT repeatedly
 * pester.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Flame } from 'lucide-react';
import { toast } from 'sonner';
import { bookstoreApi } from '@/lib/bookstoreApi';

const HEAT_OPTIONS = [
  { level: 1, label: 'Sweet',      note: 'kisses, closed door, minimal spice' },
  { level: 2, label: 'Warm',       note: 'flirtation and tension, fade-to-black' },
  { level: 3, label: 'Steamy',     note: 'on-page but tasteful' },
  { level: 4, label: 'Hot',        note: 'frequent, explicit scenes' },
  { level: 5, label: 'Anything',   note: 'no filter — surprise me' },
];

export const HEAT_PROMPT_MARKER = 'bs-heat-prompt-shown';

export function shouldShowHeatPrompt(user) {
  if (!user) return false;
  if (typeof user.preferred_heat_max === 'number') return false;
  try {
    if (localStorage.getItem(HEAT_PROMPT_MARKER) === '1') return false;
  } catch { /* SSR */ }
  return true;
}

export default function HeatPreferenceDialog({ onDone }) {
  const [busy, setBusy] = useState(false);

  const pick = async (level) => {
    setBusy(true);
    try {
      await bookstoreApi.updatePreferences({ preferred_heat_max: level });
      try { localStorage.setItem(HEAT_PROMPT_MARKER, '1'); } catch {}
      toast.success(level === 5
        ? 'Filter cleared — every heat level will be shown.'
        : `Saved — shelves will default to heat ${level} and below.`);
      onDone?.(level);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save your preference.');
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    try { localStorage.setItem(HEAT_PROMPT_MARKER, '1'); } catch {}
    onDone?.(null);
  };

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="bs-heat-prompt"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-xl border border-oxblood/40 bg-card text-foreground shadow-2xl">
        <div className="flex items-center gap-2 border-b border-border bg-panel-header/60 px-5 py-3">
          <Flame className="h-4 w-4 text-orange-500" fill="currentColor" />
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Reader profile</div>
        </div>
        <div className="px-6 py-5">
          <h2 className="text-lg font-semibold tracking-tight">
            How spicy do you like it?
          </h2>
          <p className="mt-1 text-[12.5px] text-muted-foreground">
            Pick a maximum heat level and every shelf will pre-filter to books
            you're comfortable with. Change it any time in your profile.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {HEAT_OPTIONS.map((opt) => (
              <button
                key={opt.level}
                type="button"
                disabled={busy}
                onClick={() => pick(opt.level)}
                data-testid={`bs-heat-prompt-option-${opt.level}`}
                className="group flex flex-col items-start gap-1 rounded-md border border-border bg-background px-3 py-2.5 text-left transition hover:-translate-y-0.5 hover:border-oxblood/70 hover:shadow-md disabled:opacity-60"
              >
                <span className="flex items-center gap-1">
                  {Array.from({ length: opt.level }).map((_, i) => (
                    <Flame key={i} className="h-3.5 w-3.5 text-orange-500" fill="currentColor" strokeWidth={2} />
                  ))}
                  <span className="ml-1 text-[13.5px] font-semibold text-foreground">
                    {opt.label}
                  </span>
                </span>
                <span className="text-[11px] text-muted-foreground group-hover:text-foreground">
                  {opt.note}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>You can change this in your profile.</span>
            <button
              type="button"
              onClick={dismiss}
              data-testid="bs-heat-prompt-skip"
              className="rounded-sm px-2 py-1 hover:bg-secondary hover:text-foreground"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
